import { message } from 'antd';
import ApproveApi from '~/repositories/approveTransaction';
import * as MessageService from '~/services/message';

import { LoadingAction, decreaseLoading, increaseLoading } from './loading';
import {
  TransactionApprovalType,
  TransactionStatus,
  TransactionRequestPendingApprovalStatus,
} from '@portal/enum/approvalsType';
import {
  GET_APPROVE_TRANSACTION,
  GET_APPROVE_TRANSACTION_BY_ID,
  GET_EXECUTED_TRANSACTION,
  GET_REPROVED_TRANSACTION,
  SET_DETAILS,
} from './actionTypes';
import { Dispatch } from 'redux';
import centralNavigationService from '~/services/navigation';
import { getRouteStackPath } from '@portal/config/routes';
import { TransactionApprovalsPostType } from '@portal/models/module';
import { translate } from '@portal/services/i18n';

export const approvalsTransactions =
  (type: TransactionApprovalType, id: string, accountId: string, pin: string) =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await ApproveApi.approvalsTransaction(type, id, pin);

      if (payload.status === TransactionStatus.EXECUTED) {
        if (!payload?.id) {
          centralNavigationService.navigate(
            `${getRouteStackPath('HOME', 'MY_ACCOUNT_DETAILS')}state`,
            {
              isExtract: true,
            }
          );

          const formatted = {
            id: id,
            accountId: accountId,
            bankId: payload.coreBankId || payload.idOperationCredit,
            type: payload.type,
            transactionAt: payload.transactionAt,
            amount: payload.amount,
            payerId: payload.p2pInfo?.payeeId,
            paymentAccountType: 1,
            status: payload.status,
            shared: {
              origin: {
                userName: payload.origin.account.name,
                userTaxIdentifier: payload.origin.account.taxIdentifier,
                userBankAccount: payload.origin.paymentAccount.bankAccount,
                userBankBranch: payload.origin.paymentAccount.bankBranchAccount,
              },
              destiny: {
                userName: payload.destiny.account.name,
                userTaxIdentifier: payload.destiny.account.taxIdentifier,
                userBankAccount: payload.destiny.paymentAccount.bankAccount,
                userBankBranch:
                  payload.destiny.paymentAccount.bankBranchAccount,
              },
            },
            p2pInfo: {
              payeeName: payload.destiny.account.name,
              payeeTaxIdentifier:
                payload.p2pInfo.taxIdentifier ||
                payload.destiny.account.taxIdentifier,
              payerName: payload.origin.account.name,
              payerTaxIdentifier: payload.origin.account.taxIdentifier,
            },
          };

          dispatch({
            type: SET_DETAILS,
            payload: {
              error: null,
              transaction: formatted,
              type: null,
            },
          });

          return;
        }

        centralNavigationService.navigate(
          `${getRouteStackPath('HOME', 'MY_ACCOUNT_DETAILS')}${payload?.id}`,
          { isExtract: true }
        );
      } else if (payload.status === TransactionStatus.SCHEDULE) {
        if (id && accountId) {
          MessageService.success('PAGES.HOME.APPROVED.TABLE.SUCCESS');

          centralNavigationService.navigate(
            `${getRouteStackPath('HOME', 'MY_ACCOUNT_DETAILS')}${
              payload?.transactionRequestId
                ? payload?.transactionRequestId
                : payload?.id
            }`,
            { isExtract: true, isScheduled: true }
          );
        }
      } else if (payload.status === TransactionStatus.PENDING_APPROVE) {
        MessageService.success('PAGES.HOME.APPROVED.TABLE.SUCCESS');
        centralNavigationService.navigate(
          getRouteStackPath('APPROVED', 'APPROVED_PAGE')
        );
      } else {
        centralNavigationService.navigate(
          getRouteStackPath('APPROVED', 'APPROVED_PAGE')
        );
        MessageService.success('PAGES.HOME.APPROVED.TABLE.SUCCESS');
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
        if (id && accountId) {
          dispatch(getByIdApprovalTransactions(id));
        }
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const approvalsMultipleTransactions =
  (
    params: TransactionApprovalsPostType,
    callback: (err: Error | null) => void
  ) =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await ApproveApi.approvalsMultipleTransaction(params);

      const hasErrors =
        payload?.error &&
        Array.isArray(payload.error) &&
        payload.error.length > 0;
      const hasSuccess =
        payload?.success &&
        Array.isArray(payload.success) &&
        payload.success.length > 0;

      if (hasErrors && !hasSuccess) {
        message.error(translate('PAGES.HOME.APPROVED.TABLE.ERROR'));
      } else if (hasErrors && hasSuccess) {
        MessageService.warn(
          translate('PAGES.HOME.APPROVED.TABLE.PARTIAL_SUCCESS')
        );
      } else if (hasSuccess) {
        MessageService.success('PAGES.HOME.APPROVED.TABLE.SUCCESS');
      }

      dispatch({
        payload: true,
        type: 'MULTIPLE_APPROVAL_SUCCESS',
      });

      dispatch({
        payload: payload,
        type: 'MULTIPLE_APPROVAL_RESULT',
      });
      callback(null);
    } catch (err) {
      callback(err as Error);
    } finally {
      dispatch({
        payload: false,
        type: 'MULTIPLE_APPROVAL_SUCCESS',
      });
      dispatch(decreaseLoading());
    }
  };

export const getApprovalTransactions =
  (params: advancedFilterModels.ApprovalTransactionFilter) =>
  async (dispatch: Dispatch<LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payload = await ApproveApi.getTransaction(params);
      const { status } = params;

      const doesStatusIsExecutedOrScheduled =
        Array.isArray(status) &&
        status.some(
          (statusCode) =>
            statusCode === TransactionStatus.EXECUTED ||
            statusCode === TransactionStatus.SCHEDULE
        );

      if (
        doesStatusIsExecutedOrScheduled ||
        status === TransactionRequestPendingApprovalStatus.APPROVED
      ) {
        dispatch({
          payload,
          type: GET_EXECUTED_TRANSACTION,
        });
        return;
      }

      if (status === TransactionRequestPendingApprovalStatus.PENDING) {
        dispatch({
          payload,
          type: GET_APPROVE_TRANSACTION,
        });
        return;
      }

      if (status === TransactionRequestPendingApprovalStatus.REPROVED) {
        dispatch({
          payload,
          type: GET_REPROVED_TRANSACTION,
        });
        return;
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getByIdApprovalTransactions =
  (id: string) => async (dispatch: Dispatch<LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const response = await ApproveApi.getByIdTransaction(id);
      const payload = {
        ...response,
        amount: response.transactionRequest?.amount,
        type: response.transactionRequest?.type,
        favoredTax: response.transactionRequest?.favored?.taxIdentifier,
        favoredName: response.transactionRequest?.favored?.alias,
        partnerBankAgency: response.transactionRequest?.favored?.bankAgency,
        partnerBankName: response.transactionRequest?.favored?.bankUserName,
        favored: response.transactionRequest?.favored,
        account: response.transactionRequest?.account,
        transactionRequestPendingApprovalId: response.transactionRequestId,
      };

      dispatch({
        payload,
        type: GET_APPROVE_TRANSACTION_BY_ID,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
